import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Instagram, Facebook, MessageCircle, Phone, Mail, MapPin, Lock } from "lucide-react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useDynamicLogo } from "@/lib/useLogo";
import PaymentBadges from "@/components/site/PaymentBadges";

function abs(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API}${url.startsWith("/api") ? url.slice(4) : url}`;
}

export default function Footer() {
  const { t, trSetting, lang } = useI18n();
  const [s, setS] = useState({});
  const [blocks, setBlocks] = useState([]);
  const [partners, setPartners] = useState([]);
  const logoUrl = useDynamicLogo();

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/content-blocks").then((r) => setBlocks(r.data)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  const addr = trSetting(s, "address");
  const phone = trSetting(s, "phone");
  const email = trSetting(s, "email");
  const ig = s?.instagram?.value;
  const fb = s?.facebook?.value;
  const wa = s?.whatsapp?.value; // raw number
  const waUrl = wa ? `https://wa.me/${wa.replace(/[^0-9]/g, "")}` : "";
  const siteName = s?.site_name?.value || "Berdhill Rooms";
  const copyright = s?.copyright_text?.value || `© ${new Date().getFullYear()} ${siteName}. All rights reserved.`;

  function blockTitle(b) {
    return b?.i18n?.[lang]?.title ?? b.title;
  }

  return (
    <footer data-testid="site-footer" className="bg-[#231F20] text-[#E6E2DB] mt-24">
      {partners.length > 0 && (
        <div className="border-b border-white/10">
          <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-10">
            <div className="text-xs uppercase tracking-[0.3em] text-[#bcb5a8] mb-6 text-center">Trusted partners</div>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-14">
              {partners.map((p) => {
                const inner = (
                  <img
                    src={abs(p.logo_url)}
                    alt={p.name}
                    title={p.name}
                    className="h-7 md:h-9 max-w-[140px] object-contain opacity-80 hover:opacity-100 transition-opacity grayscale hover:grayscale-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                );
                return p.url ? (
                  <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`partner-${p.id}`}>
                    {inner}
                  </a>
                ) : <span key={p.id} data-testid={`partner-${p.id}`}>{inner}</span>;
              })}
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <img src={logoUrl} alt={siteName} className="h-14" />
          <p className="mt-6 text-sm leading-relaxed text-[#bcb5a8] max-w-sm">{t("footer.tagline")}</p>
          <div className="mt-5 text-sm space-y-1 text-[#bcb5a8]">
            {addr && <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0" /> <span>{addr}</span></div>}
            {phone && <div className="flex items-center gap-2"><Phone size={14} /> <a href={`tel:${phone.replace(/\s/g, "")}`} className="hover:text-white">{phone}</a></div>}
            {email && <div className="flex items-center gap-2"><Mail size={14} /> <a href={`mailto:${email}`} className="hover:text-white">{email}</a></div>}
          </div>
          <div className="mt-5 flex items-center gap-3">
            {ig && (
              <a href={ig} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="bg-white/10 hover:bg-brand-primary text-white p-2.5 rounded-sm transition-colors">
                <Instagram size={16} />
              </a>
            )}
            {fb && (
              <a href={fb} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="bg-white/10 hover:bg-brand-primary text-white p-2.5 rounded-sm transition-colors">
                <Facebook size={16} />
              </a>
            )}
            {waUrl && (
              <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" aria-label="WhatsApp" className="bg-white/10 hover:bg-emerald-600 text-white p-2.5 rounded-sm transition-colors">
                <MessageCircle size={16} />
              </a>
            )}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] mb-4 text-[#bcb5a8]">{t("footer.explore")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link data-testid="footer-link-rooms" to="/rooms" className="hover:text-white">{t("nav.rooms")}</Link></li>
            <li><Link to="/reviews" className="hover:text-white">{t("nav.reviews")}</Link></li>
            <li><Link to="/about" className="hover:text-white">{t("nav.about")}</Link></li>
            <li><Link to="/contact" className="hover:text-white">{t("nav.contact")}</Link></li>
            <li><Link to="/my-bookings" className="hover:text-white">{t("nav.mybookings")}</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] mb-4 text-[#bcb5a8]">Guest info</div>
          <ul className="space-y-2 text-sm">
            {blocks.slice(0, 7).map((b) => (
              <li key={b.section_key}>
                <Link data-testid={`footer-info-${b.section_key}`} to={`/info/${b.section_key}`} className="hover:text-white">{blockTitle(b)}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] mb-4 text-[#bcb5a8]">{t("footer.policies")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/policies/cancellation" className="hover:text-white">Cancellation</Link></li>
            <li><Link to="/policies/house-rules" className="hover:text-white">House rules</Link></li>
            <li><Link to="/policies/payment" className="hover:text-white">Payment</Link></li>
            <li><Link to="/policies/privacy" className="hover:text-white">Privacy</Link></li>
            <li><Link to="/policies/terms" className="hover:text-white">Terms</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-6 text-xs text-[#8a8478] flex flex-col md:flex-row justify-between gap-4 items-center">
          <span>{copyright}</span>
          <PaymentBadges size="sm" className="opacity-90" />
          <div className="flex items-center gap-4">
            <span>Sarandë, Albania · Ionian Sea</span>
            <Link to="/staff/login" data-testid="footer-staff-link" className="inline-flex items-center gap-1 hover:text-white opacity-60 hover:opacity-100 transition-opacity">
              <Lock size={11} /> Staff
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
